import {
  QuestionGroupType,
  useGetQuestionGroupsByQBankStructureLevelItemIdLazyQuery,
} from '@exam-management/graphql';
import {
  type FilterOptionsState,
  type FormSearchSelectionProps,
  type OptionSelectionProps,
  createFilterOptions,
  FormSearchSelection,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useCallback, useEffect, useState } from 'react';
import { useController, type FieldValues } from 'react-hook-form';

interface LOQOption extends OptionSelectionProps {
  inputValue?: string;
}

export interface LotOfQuestionFieldProps<TFieldValues extends FieldValues>
  extends Omit<FormSearchSelectionProps<TFieldValues>, 'options' | 'label'> {
  subTopicLevelItemId: string;
}

export default function LotOfQuestionField<TFieldValues extends FieldValues>(
  props: Readonly<LotOfQuestionFieldProps<TFieldValues>>,
) {
  const { subTopicLevelItemId, name, ...restProps } = props;
  const [isOpen, { open, close }] = useDisclosure();
  const [options, setOptions] = useState<LOQOption[]>([]);
  const [customOptions, setCustomOptions] = useState<LOQOption[]>([]);

  const { field } = useController({ ...props, name: props.name });

  const [getQuestionGroups, { loading: isGetQuestionGroupsLoading }] =
    useGetQuestionGroupsByQBankStructureLevelItemIdLazyQuery({
      onCompleted: (data) => {
        const questionGroupOptions = data.qctGetQuestionGroupsByQBankStructureLevelItemId.map(
          (questionGroup) => ({
            label: questionGroup.name,
            value: questionGroup.name,
          }),
        );

        const filteredCustomOptions = customOptions.filter((customOption) =>
          questionGroupOptions.every((option) => option.value !== customOption.value),
        );

        setOptions([...filteredCustomOptions, ...questionGroupOptions]);
      },
      fetchPolicy: 'network-only',
    });

  const handleOpenOptions = useCallback(async () => {
    if (subTopicLevelItemId) {
      await getQuestionGroups({
        variables: {
          input: {
            qBankStructureLevelItemId: subTopicLevelItemId,
            type: QuestionGroupType.LotOfQuestion,
          },
        },
      });
    } else {
      setOptions(customOptions);
    }
    open();
  }, [subTopicLevelItemId, getQuestionGroups]);

  const filter = createFilterOptions<LOQOption>();

  const allowCreationFilterOptions = useCallback(
    (options: LOQOption[], params: FilterOptionsState<LOQOption>) => {
      if (isGetQuestionGroupsLoading) {
        return options;
      }

      const filtered = filter(options, params);
      const { inputValue } = params;
      const trimmedInputValue = inputValue.trim();
      const isExisting = options.some((option) => trimmedInputValue === option.label);
      if (trimmedInputValue !== '' && !isExisting) {
        filtered.push({
          label: `Add "${trimmedInputValue}"`,
          value: trimmedInputValue,
          inputValue: trimmedInputValue,
        });
      }
      return filtered;
    },
    [],
  );

  const getNoOptionsText = () => {
    if (isGetQuestionGroupsLoading) {
      return 'กำลังโหลดข้อมูล...';
    }

    return 'กรุณากรอกชื่อ เพื่อสร้าง LOQ ใหม่';
  };

  const noOptionsText = getNoOptionsText();

  useEffect(() => {
    if (!subTopicLevelItemId) {
      field.onChange(undefined);
    }
  }, [subTopicLevelItemId]);

  return (
    <FormSearchSelection
      label='เลือก LOQ'
      placeholder='เลือก LOQ / พิมพ์เพื่อสร้าง LOQ ใหม่'
      name={name}
      options={options.length === 0 ? customOptions : options}
      noOptionsText={noOptionsText}
      loading={isGetQuestionGroupsLoading}
      open={isOpen}
      onOpen={handleOpenOptions}
      onClose={close}
      filterOptions={allowCreationFilterOptions}
      getOptionLabel={(option: LOQOption) => {
        if (typeof option === 'string') {
          return option;
        }
        if (option.inputValue) {
          return option.inputValue;
        }
        return option.label;
      }}
      onChange={(_, value, reason) => {
        if (reason === 'selectOption') {
          const selectedOptionValue = value?.value;
          if (!options.some((option) => option.value === selectedOptionValue)) {
            setOptions((prev) => [
              ...prev,
              {
                label: selectedOptionValue,
                value: selectedOptionValue,
                inputValue: selectedOptionValue,
              } as LOQOption,
            ]);
            setCustomOptions((prev) => [
              ...prev,
              {
                label: selectedOptionValue,
                value: selectedOptionValue,
                inputValue: selectedOptionValue,
              } as LOQOption,
            ]);
          }
        }
      }}
      {...restProps}
    />
  );
}
